import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, DOCUMENT } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { SiteHeaderComponent } from '../ui/site-header.component';
import { SiteFooterComponent } from '../ui/site-footer.component';
import { CookieBannerComponent } from '../ui/cookie-banner.component';
import { isSiteRoute } from '../../core/utils/public-route';
import { SeoService } from '../seo/seo.service';
import { ensureFontPreloads } from './font-preload';

/// Çapa kaydırması (anchorScrolling) CSS scroll-margin-top'u yok sayar
/// (ViewportScroller window.scrollTo kullanır); başlık yapışkan üst bilginin
/// altında kalmasın diye üst bilgi yüksekliği + boşluk kadar ofset.
const ANCHOR_GAP_PX = 16;
const FALLBACK_ANCHOR_OFFSET_PX = 80;

export function anchorOffset(doc: Document): [number, number] {
  const header = doc.querySelector('site-header header');
  const height = header?.getBoundingClientRect().height;
  return [0, height ? Math.ceil(height) + ANCHOR_GAP_PX : FALLBACK_ANCHOR_OFFSET_PX];
}

/// Herkese açık sitenin kökü. `.mypt-site` sınıfı site belirteçlerini ve
/// Tailwind yardımcılarını (yalnız bu kök altında) etkinleştirir.
///
/// `<main id="main-content">`: site rotalarında AppComponent <main> eklemez,
/// layout ekler. Uygulama rotası öneki altında bilinmeyen yol (örn.
/// `/dashboard/unknown`) 404 olarak buraya düşer; orada AppComponent/kabuk
/// zaten <main> çizdiği için iç içe ikinci <main> yerine düz <div> kullanılır.
@Component({
    selector: 'site-public-layout',
    imports: [RouterOutlet, SiteHeaderComponent, SiteFooterComponent, CookieBannerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'mypt-site' },
    template: `
    <site-header />
    @if (ownsMain()) {
      <main id="main-content" tabindex="-1" class="flex-1">
        <router-outlet />
      </main>
    } @else {
      <div class="flex-1">
        <router-outlet />
      </div>
    }
    <site-footer />
    <site-cookie-banner />
  `
})
export class PublicLayoutComponent {
  private readonly router = inject(Router);
  readonly ownsMain = signal(isSiteRoute(this.router.url));

  constructor() {
    const doc = inject(DOCUMENT);
    ensureFontPreloads(doc);
    // Uygulama sayfalarına geçince site SEO'su (canonical, JSON-LD) kalkar;
    // çapa ofseti de sıfırlanır (uygulama kaydırması değişmesin).
    const seo = inject(SeoService);
    const scroller = inject(ViewportScroller);
    scroller.setOffset(() => anchorOffset(doc));
    inject(DestroyRef).onDestroy(() => {
      seo.reset();
      scroller.setOffset([0, 0]);
    });
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.ownsMain.set(isSiteRoute(e.urlAfterRedirects)));
  }
}
